import { useState, useMemo, useCallback } from 'react';

// When album_title is primary and disc/track aren't already in the ordering stack,
// use these as default tiebreakers so albums show in natural order.
const DEFAULT_SECONDARIES = {
  album_title: [
    { field: 'disc_number', dir: 'asc' },
    { field: 'track_number', dir: 'asc' },
  ],
};

function compareVal(a, b, field, dir) {
  let aVal = a[field];
  let bVal = b[field];
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;
  if (typeof aVal === 'string') aVal = aVal.toLowerCase();
  if (typeof bVal === 'string') bVal = bVal.toLowerCase();
  if (aVal < bVal) return dir === 'asc' ? -1 : 1;
  if (aVal > bVal) return dir === 'asc' ? 1 : -1;
  return 0;
}

export default function useSortable(items, defaultField = null) {
  // ordering = array of {field, dir}; first entry is primary, rest are secondaries (retain their direction)
  const [ordering, setOrdering] = useState(
    defaultField ? [{ field: defaultField, dir: 'asc' }] : []
  );

  const handleSort = useCallback((field) => {
    setOrdering(prev => {
      const primaryField = prev[0]?.field;
      if (primaryField === field) {
        // Toggle direction on the primary, keep secondaries unchanged
        const toggled = prev[0].dir === 'asc' ? 'desc' : 'asc';
        return [{ field, dir: toggled }, ...prev.slice(1)];
      }
      // Promote field to front, retaining its previous direction if it was a secondary
      const existing = prev.find(o => o.field === field);
      const without = prev.filter(o => o.field !== field);
      return [{ field, dir: existing?.dir ?? 'asc' }, ...without];
    });
  }, []);

  const sorted = useMemo(() => {
    if (!ordering.length) return items;
    const [primary, ...explicitSecondaries] = ordering;
    const defaults = DEFAULT_SECONDARIES[primary.field] || [];
    // Fill in defaults only for fields not already tracked as secondaries
    const fillDefaults = defaults.filter(d => !explicitSecondaries.some(s => s.field === d.field));
    const effectiveOrdering = [primary, ...explicitSecondaries, ...fillDefaults];

    return [...items].sort((a, b) => {
      for (const { field, dir } of effectiveOrdering) {
        const c = compareVal(a, b, field, dir);
        if (c !== 0) return c;
      }
      return 0;
    });
  }, [items, ordering]);

  const SortIcon = ({ field }) => {
    const primary = ordering[0];
    if (!primary || primary.field !== field) {
      return <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>⇅</span>;
    }
    return <span style={{ color: 'var(--accent)', marginLeft: 4 }}>{primary.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  return {
    sorted,
    handleSort,
    sortField: ordering[0]?.field ?? null,
    sortDir: ordering[0]?.dir ?? 'asc',
    SortIcon,
  };
}
